// Permission requirement detection and request helpers.
// Camera access is NOT requested here — it is opened once by startARSession().
// Only platform-gated permissions that must come from a user gesture before
// the AR session starts are handled here (iOS motion sensors).

export type RequiredPermissions = {
  camera: true;     // always required; opened by startARSession
  motion: boolean;  // orientation/motion sensors require an explicit prompt
};

// ── Detection ─────────────────────────────────────────────────────────────────

/**
 * Returns which permissions require an explicit pre-flight prompt.
 *
 * Motion detection rationale:
 *   iOS 13+ gates DeviceOrientationEvent and DeviceMotionEvent behind
 *   permission prompts that must be triggered from a user gesture. XR8's SLAM
 *   uses devicemotion acceleration/gyro samples for 6DOF VIO, so both request
 *   APIs need to be considered here.
 *   Android/Chrome exposes orientation events freely — no prompt needed there.
 */
export function getRequiredPermissions(): RequiredPermissions {
  const orientationGated =
    typeof DeviceOrientationEvent !== 'undefined' &&
    typeof (DeviceOrientationEvent as unknown as { requestPermission?: unknown })
      .requestPermission === 'function';

  const motionGated =
    typeof DeviceMotionEvent !== 'undefined' &&
    typeof (DeviceMotionEvent as unknown as { requestPermission?: unknown })
      .requestPermission === 'function';

  return { camera: true, motion: orientationGated || motionGated };
}

// ── Copy builder ──────────────────────────────────────────────────────────────

export function buildPermissionCopy(perms: RequiredPermissions): string {
  if (perms.camera && perms.motion) {
    return (
      'This experience needs access to your camera and device motion sensors ' +
      'to place a life-sized whale in your surroundings. ' +
      'Tap below to grant access and begin.'
    );
  }
  if (perms.motion) {
    return (
      'This experience needs access to your device motion sensors ' +
      'to orient the whale in your surroundings. ' +
      'Tap below to grant access and begin.'
    );
  }
  return (
    'This experience needs access to your camera ' +
    'to place a life-sized whale in your surroundings. ' +
    'Tap below to grant access and begin.'
  );
}

// ── Pre-flight motion request ─────────────────────────────────────────────────

/**
 * Requests device orientation/motion permissions on platforms that require
 * them (iOS 13+). Must be called from within a user-gesture handler.
 * Returns true if granted or not required, false if denied.
 */
export async function requestMotionPermissionIfNeeded(
  perms: RequiredPermissions,
): Promise<boolean> {
  if (!perms.motion) return true;
  try {
    const orientationRequest = (
      DeviceOrientationEvent as unknown as {
        requestPermission?: () => Promise<PermissionState>;
      }
    ).requestPermission;
    const motionRequest = (
      DeviceMotionEvent as unknown as {
        requestPermission?: () => Promise<PermissionState>;
      }
    ).requestPermission;

    const requests: Promise<PermissionState>[] = [];
    if (typeof orientationRequest === 'function') {
      requests.push(orientationRequest());
    }
    if (typeof motionRequest === 'function') {
      requests.push(motionRequest());
    }

    if (requests.length === 0) return true;

    const results = await Promise.all(requests);
    return results.every((result) => result === 'granted');
  } catch {
    return false;
  }
}
